import {useState} from 'react';
import {ArticleListResponse} from "../models/interfaces/article-list-response.interface";
import {CreateArticleRequest} from "../models/interfaces/create-article-request.interface";
import {UpdateArticleRequest} from "../models/interfaces/update-article-request.interface";
import ArticleApiService from "../services/article-api.service";
import {ArticleDTO} from "../models/interfaces/article-dto.interface";
import {useLoading} from "./use-loading.hook";
import {LoadingKeys} from "../models/enums/loading-keys.enum";
import {ArticleDetails} from "../models/interfaces/article-details.interface";
import {ArticleListRequestParams} from "../models/interfaces/article-list-request-params.interface";
import {mapUiParamsToApiParams} from "../../articles/helpers/article-params-mapper";
import {FileUploadResponse} from "../models/interfaces/file-upload-response.interface";
import {AiGeneratedArticlePayload} from "../../articles/models/interfaces/ai-generated-article.interface";
import {getMockAiNewArticle} from "../../../mocks/articles/ai-generated-article.mock";
import {CustomError, isCustomError} from "../services/error-handling";
import {isAxiosError} from "axios";
import {mapArticleDetailsToSchema} from "../../articles/helpers/map-article-details-to-schema";
import {mapArticleSchemaToUpdate} from "../../articles/helpers/map-article-schema-to-update";
import {mapArticleSchemaToCreate} from "../../articles/helpers/map-article-schema-to-create";

const shouldUseAiMocks = (process.env.REACT_APP_USE_AI_MOCKS ?? 'false') !== 'false';

export const useArticleApi = () => {
    const loadingKey = LoadingKeys.USE_ARTICLE_API;
    const {startLoading, stopLoading} = useLoading();
    const [error, setError] = useState<string | null>(null);

    const getSearchArticles = async (params: ArticleListRequestParams = {}): Promise<ArticleListResponse> => {
        startLoading([loadingKey, "getSearchArticles"]);
        setError(null);
        try {
            const apiParams = mapUiParamsToApiParams(params);
            return await ArticleApiService.getSearchArticles(apiParams);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getSearchArticles"]);
        }
    };

    const getArticle = async (id: string): Promise<ArticleDetails> => {
        startLoading([loadingKey, "getArticle"]);
        setError(null);
        try {
            return await ArticleApiService.getArticleById(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getArticle"]);
        }
    };

    const createArticle = async (data: CreateArticleRequest): Promise<ArticleDTO> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.createArticle(data);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const updateArticle = async (data: UpdateArticleRequest): Promise<ArticleDetails> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.updateArticle(data);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const deleteArticle = async (id: string): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.deleteArticle(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const publishArticle = async (body: UpdateArticleRequest): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.publishArticle(body);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const resetVersionsToPublished = async (id: string): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.resetVersionsToPublished(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const uploadTempFile = async (file: File): Promise<FileUploadResponse> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.uploadTempFile(file);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const getUniversalArticle = async (): Promise<ArticleDetails | null> => {
        startLoading([loadingKey, "getUniversalArticle"]);
        setError(null);
        try {
            return await ArticleApiService.getUniversalArticle();
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getUniversalArticle"]);
        }
    };

    const getPostoperativeUniversalArticle = async (): Promise<ArticleDetails | null> => {
        startLoading([loadingKey, "getPostoperativeUniversalArticle"]);
        setError(null);
        try {
            return await ArticleApiService.getPostoperativeUniversalArticle();
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getPostoperativeUniversalArticle"]);
        }
    };

    const getPublicArticle = async (id: string, appId: string): Promise<ArticleDetails> => {
        startLoading([loadingKey, "getPublicArticle"]);
        setError(null);
        try {
            return await ArticleApiService.getPublicArticle(id, appId);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getPublicArticle"]);
        }
    };

    const shareArticle = async (id: string, phoneNumber: string): Promise<ArticleDetails> => {
        startLoading([loadingKey, "shareArticle"]);
        setError(null);
        try {
            return await ArticleApiService.shareArticle(id, phoneNumber);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "shareArticle"]);
        }
    };

    const setUniversalArticle = async (id: string): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.setUniversalArticle(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const setPostoperativeUniversalArticle = async (id: string): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await ArticleApiService.setPostOperativeUniversalArticle(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const generateArticleFromDocuments = async (files: File[]): Promise<AiGeneratedArticlePayload> => {
        startLoading([loadingKey, "generateArticleFromDocuments"]);
        setError(null);
        try {
            if (shouldUseAiMocks) {
                return await getMockAiNewArticle(files.map(f => f.name).join(", "));
            }
            return await ArticleApiService.generateArticleFromDocuments(files);
        } catch (err: any) {
            if (isAxiosError(err) && isCustomError(err.response?.data)) {
                const error = err.response?.data as CustomError;
                setError(error.message);
            } else {
                setError(err.message);
            }
            throw err;
        } finally {
            stopLoading([loadingKey, "generateArticleFromDocuments"]);
        }
    };

    const improveArticleWithAi = async (id: string): Promise<AiGeneratedArticlePayload> => {
        startLoading([loadingKey, "improveArticleWithAi"]);
        setError(null);
        try {
            return await ArticleApiService.improveArticleWithAi(id);
        } catch (err: any) {
            if (isAxiosError(err) && isCustomError(err.response?.data)) {
                const error = err.response?.data as CustomError;
                setError(error.message);
            } else {
                setError(err.message);
            }
            throw err;
        } finally {
            stopLoading([loadingKey, "improveArticleWithAi"]);
        }
    };

    const hideArticle = async (article: ArticleDetails): Promise<ArticleDetails> => {
        startLoading([loadingKey, "hideArticle"]);
        setError(null);
        try {
            const schema = mapArticleDetailsToSchema(article);
            const updateRequest = mapArticleSchemaToUpdate(schema);
            return await ArticleApiService.updateArticle({
                ...updateRequest,
                id: article.id,
                isVisible: false,
            });
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "hideArticle"]);
        }
    };

    const publishArticleFromList = async (article: ArticleDetails): Promise<void> => {
        startLoading([loadingKey, "publishArticleFromList"]);
        setError(null);
        try {
            const schema = mapArticleDetailsToSchema(article);
            const request = mapArticleSchemaToCreate(schema);
            return await ArticleApiService.publishArticle({
                ...request,
                id: article.id,
                isVisible: true,
            });
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "publishArticleFromList"]);
        }
    };

    return {
        error,
        setError,
        getSearchArticles,
        getArticle,
        createArticle,
        updateArticle,
        deleteArticle,
        publishArticle,
        resetVersionsToPublished,
        uploadTempFile,
        getUniversalArticle,
        setUniversalArticle,
        getPostoperativeUniversalArticle,
        setPostoperativeUniversalArticle,
        getPublicArticle,
        shareArticle,
        generateArticleFromDocument: generateArticleFromDocuments,
        improveArticleWithAi,
        hideArticle,
        publishArticleFromList
    };
};
